import {
  apiCreateAnnouncement,
  apiGetOtherAnnouncementsTemplate,
} from "@app/api/announcements/announcements.api";
import { DataDialog } from "@app/components/atoms";
import {
  AnnouncementType,
  Platform,
  SupportedLanguageNames,
} from "@app/constants";
import { isValidUrl, isValidVersion } from "@app/helpers";
import { store, setShowAlert } from "@app/store";
import {
  FormControl,
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
  TextField,
  MenuItem,
  Button,
  Box,
  Grid,
  InputLabel,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { format } from "date-fns";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  AnnouncementFormOther,
  AnnouncementTypeFilter,
  CreateAnnouncementProps,
} from "@app/types";
import * as XLSX from "xlsx";

const platforms = Object.values(Platform);
const announcementTypes: AnnouncementTypeFilter[] = Object.values(
  AnnouncementType
).map((type) => ({
  label: `announcementTypeValues.${type.toLowerCase()}`,
  key: type.toLowerCase(),
  announcementType: type,
}));

const CreateAnnouncementDialog: React.FC<CreateAnnouncementProps> = ({
  createDialogOpen,
  setCreateDialogOpen,
  setRefreshAnnouncements,
  timeFormat = "yyyy/MM/dd HH:mm",
}) => {
  const { t } = useTranslation();
  const dateTimeFormat = timeFormat || "yyyy/MM/dd HH:mm";
  const RTL_LANGS = ["ar", "ur"];
  const isRTL = (lang: string) => RTL_LANGS.includes(lang);
  const [form, setForm] = useState<Record<string, string | Date | null>>({
    type: AnnouncementType.DOWNTIME,
    popupEndTime: null,
    fromUTCtime: null,
    toUTCtime: null,
    link: "",
    title: "",
    body: "",
    version: "",
    platform: "",
    isoLocaleCode: "",
  });

  const languages = useMemo(
    () =>
      Object.entries(SupportedLanguageNames).map(([code, label]) => ({
        code,
        label,
      })),
    []
  );

  const [langData, setLangData] = useState<
    Record<string, AnnouncementFormOther>
  >(() =>
    languages.reduce((acc, l) => {
      acc[l.code] = { title: "", body: "", link: "" };
      return acc;
    }, {} as Record<string, AnnouncementFormOther>)
  );

  const [errors, setErrors] = useState({
    popupEndTime: "",
    fromUTCtime: "",
    toUTCtime: "",
  });

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<AnnouncementType>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLangFieldChange = useCallback(
    (lang: string, field: "title" | "body" | "link", value: string) => {
      setLangData((prev) => ({
        ...prev,
        [lang]: { ...prev[lang], [field]: value },
      }));
    },
    []
  );

  const validateDates = (
    field: string,
    value: Date,
    comparisonValue?: Date | null
  ) => {
    let msg = "";
    if (form[field] && new Date(value) <= new Date()) {
      msg = t("futureDate");
    } else if (value <= new Date()) {
      msg = t("futureDate");
    } else if (
      field === "popupEndTime" &&
      ((comparisonValue && new Date(comparisonValue) > value) ||
        (!comparisonValue &&
          form.fromUTCtime &&
          new Date(form.fromUTCtime) &&
          new Date(form.fromUTCtime) > value))
    ) {
      msg = t("popupEndAfterFrom");
    } else if (
      field === "toUTCtime" &&
      form.fromUTCtime &&
      new Date(form.fromUTCtime) &&
      value <= new Date(form.fromUTCtime)
    ) {
      msg = t("toAfterFrom");
    } else if (
      field === "fromUTCtime" &&
      form.toUTCtime &&
      new Date(form.toUTCtime) &&
      new Date(form.toUTCtime) <= value
    ) {
      msg = t("toBeforeFrom");
    }

    setErrors((prev) => ({ ...prev, [field]: msg }));
  };

  const handleDateChange = (
    name: string,
    value: Date | null,
    skipErrorCheck: boolean = false,
    comparisonValue?: Date | null
  ) => {
    if (!value || isNaN(value.getTime())) {
      if (!skipErrorCheck)
        setErrors((prev) => ({ ...prev, [name]: t("invalidDate") }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: format(value, "yyyy-MM-dd'T'HH:mm:ss"),
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
    validateDates(name, value, comparisonValue);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.link && !isValidUrl(form.link as string)) return;

    const requiresDateFields = form.type === "DOWNTIME";
    const hasDateErrors =
      requiresDateFields &&
      (errors.popupEndTime || errors.fromUTCtime || errors.toUTCtime);
    if (hasDateErrors) return;

    try {
      let payload: Record<string, any> = {
        type: form.type,
      };

      if (form.type === AnnouncementType.OTHER) {
        payload.languagesData = {};

        languages.forEach((l) => {
          const ld = langData[l.code];
          if (ld && ld.title && ld.body) {
            payload.languagesData[l.code] = {
              title: ld.title,
              body: ld.body,
              link: ld.link !== "" ? ld.link : null,
            };
          }
        });
      } else if (form.type === AnnouncementType.DOWNTIME) {
        payload.fromUTCtime = form.fromUTCtime;
        payload.toUTCtime = form.toUTCtime;
        payload.popupEndTime = form.popupEndTime;
        payload.platform = form.platform;
      } else if (form.type === AnnouncementType.UPDATE) {
        payload.version = form.version;
      }

      if (
        form.type === AnnouncementType.UPDATE ||
        form.type === AnnouncementType.FEATURE
      ) {
        if (form.link !== "") payload.link = form.link;
      }
      const response = await apiCreateAnnouncement(JSON.stringify(payload));
      if (response.data) {
        store.dispatch(
          setShowAlert(
            response.data.meta.successMessage ||
              t("alerts.successfullySubmitted")
          )
        );
        resetFields();
        setCreateDialogOpen(false);
        setRefreshAnnouncements(true);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const resetFields = () => {
    setForm({
      type: AnnouncementType.DOWNTIME,
      popupEndTime: null,
      fromUTCtime: null,
      toUTCtime: null,
      link: "",
      title: "",
      body: "",
      version: "",
      platform: "",
      isoLocaleCode: "",
    });
    setErrors({ popupEndTime: "", fromUTCtime: "", toUTCtime: "" });
    setLangData(
      languages.reduce((acc, l) => {
        acc[l.code] = { title: "", body: "", link: "" };
        return acc;
      }, {} as Record<string, AnnouncementFormOther>)
    );
  };

  const handleDownloadTemplate = async () => {
    try {
      const announcementTemplate = await apiGetOtherAnnouncementsTemplate();
      const fileUrl = announcementTemplate?.data?.data?.machineTemplatePathUrl;

      if (!fileUrl) {
        store.dispatch(setShowAlert(t("ErrordownloadingExcelfile")));
        console.error("No URL found to download");
        return;
      }

      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error("Failed to fetch file");

      const blob = await response.blob();

      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);

      const urlParts = fileUrl.split("/");
      const filename =
        urlParts[urlParts.length - 1].split("?")[0] ||
        "announcement-template.xlsx";
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      store.dispatch(setShowAlert(t("ErrordownloadingExcelfile")));
      console.error("Error downloading template:", error);
    }
  };

  const handleUploadTemplate = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (e) => {
      const data = e.target?.result;
      if (!data) return;

      const workbook = XLSX.read(data, { type: "binary" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData: {
        Language: string;
        Language_Code: string;
        Title: string;
        Body: string;
        Link?: string;
      }[] = XLSX.utils.sheet_to_json(worksheet);

      let hasMissingFields = false;
      let invalidLinks: string[] = [];
      const newLangData: Record<string, AnnouncementFormOther> = {};

      jsonData.forEach((row) => {
        const langCode = row.Language_Code;
        const title = row.Title?.toString().trim() || "";
        const body = row.Body?.toString().trim() || "";
        const link = row.Link?.toString().trim() || "";

        if (!title || !body) hasMissingFields = true;

        if (link && !isValidUrl(link)) invalidLinks.push(langCode);

        newLangData[langCode] = { title, body, link };
      });

      if (hasMissingFields) {
        store.dispatch(setShowAlert(t("templateMissingFields")));
        return;
      }

      if (invalidLinks.length > 0) {
        store.dispatch(
          setShowAlert(
            t("templateInvalidLinks", { langs: invalidLinks.join(", ") })
          )
        );
        input.value = "";
        return;
      }

      setLangData(newLangData);
      store.dispatch(setShowAlert(t("templateUploadedSuccessfully")));
      input.value = "";
    };

    reader.readAsBinaryString(file);
  };

  const isOtherAnnouncementValid =
    form.type === AnnouncementType.OTHER
      ? languages.every(
          (l) =>
            !!(langData[l.code]?.title || "").toString().trim() &&
            !!(langData[l.code]?.body || "").toString().trim() &&
            (!langData[l.code]?.link || isValidUrl(langData[l.code].link))
        )
      : true;

  return (
    <DataDialog
      disableBackdropClick={true}
      title={t("createAnnouncements")}
      open={createDialogOpen}
      setOpen={setCreateDialogOpen}
      className="dialog-create-announcement"
      action={
        <div className="flex header-actions">
          {form.type === AnnouncementType.OTHER && (
            <>
              <Button
                onClick={() =>
                  document.getElementById("upload-template-input")?.click()
                }
                className="btn btn-no-border"
                style={{ width: "auto" }}
              >
                {t("uploadAnnouncementTemplate")}
              </Button>
              <input
                type="file"
                id="upload-template-input"
                accept=".xlsx, .xls"
                style={{ display: "none" }}
                onChange={handleUploadTemplate}
              />

              <Button
                onClick={() => {
                  handleDownloadTemplate();
                }}
                className="btn btn-primary ml-15"
                style={{ width: "auto" }}
              >
                {t("downloadAnnouncementTemplate")}
              </Button>
            </>
          )}
        </div>
      }
      dialogActions={
        <div className="flex align-items-center justify-content-end">
          <Button
            onClick={() => setCreateDialogOpen(false)}
            className="btn btn-no-border"
            style={{ width: "auto" }}
          >
            {t("dialogReport.cancel")}
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            className="btn btn-primary ml-15"
            disabled={
              !form.type ||
              (form.type === AnnouncementType.OTHER &&
                !isOtherAnnouncementValid) ||
              (form.type === AnnouncementType.UPDATE &&
                (!form.version || !isValidVersion(form.version as string))) ||
              (form.link && !isValidUrl(form.link as string)) ||
              (form.type === AnnouncementType.DOWNTIME &&
                (!form.popupEndTime ||
                  !form.fromUTCtime ||
                  !form.toUTCtime ||
                  !form.platform ||
                  Object.values(errors).some((err) => !!err)))
            }
          >
            {t("formAction.save")}
          </Button>
        </div>
      }
    >
      <Box
        component="form"
        autoComplete="off"
        className="mb-8"
        sx={{ py: 0.5 }}
      >
        <div className="wrapper-text-field">
          <FormControl fullWidth>
            <InputLabel id="announcement-type-label">{t("subType")}</InputLabel>
            <Select
              labelId="announcement-type-label"
              label={t("subType")}
              displayEmpty
              name="type"
              value={form.type as AnnouncementType}
              onChange={handleChange}
              renderValue={(selected: AnnouncementType) => {
                const key =
                  selected === AnnouncementType.UPDATE
                    ? "formAction.update"
                    : `announcementTypeValues.${selected.toUpperCase()}`;

                return t(key);
              }}
            >
              {announcementTypes.map((tab, index) => {
                const typeKey =
                  tab.announcementType === AnnouncementType.UPDATE
                    ? "formAction.update"
                    : `announcementTypeValues.${tab.announcementType.toUpperCase()}`;

                return (
                  <MenuItem
                    key={index}
                    value={tab.announcementType}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    {t(typeKey)}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </div>
      </Box>
      <Box
        className={`${
          form.type === AnnouncementType.OTHER
            ? ""
            : "border-radius-4 bg-gray card-min-height shadow-card"
        }`}
        sx={{ mx: "auto" }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            px:
              form.type === AnnouncementType.OTHER ? "unset" : { xs: 2, sm: 3 },
            py: form.type === AnnouncementType.OTHER ? "unset" : 2,
            margin: "auto",
          }}
        >
          <Grid
            container
            spacing={form.type === AnnouncementType.OTHER ? 1 : 2}
          >
            {form.type === AnnouncementType.OTHER && (
              <React.Suspense fallback={null}>
                {languages.map((l, idx) => (
                  <Grid item xs={12} key={l.code}>
                    <Accordion
                      defaultExpanded={idx === 0}
                      sx={{
                        "&:before": { display: "none" },
                      }}
                    >
                      <AccordionSummary
                        sx={{ backgroundColor: "#f5f6f9" }}
                        expandIcon={<ExpandMoreIcon />}
                      >
                        <Typography>{l.label} *</Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ backgroundColor: "#f5f6f9" }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6} md={4} lg={4}>
                            <TextField
                              label={t("title")}
                              placeholder={t("announcementPlaceholders.title")}
                              className="input-field"
                              name={`title_${l.code}`}
                              value={langData[l.code]?.title || ""}
                              onChange={(e) =>
                                handleLangFieldChange(
                                  l.code,
                                  "title",
                                  e.target.value
                                )
                              }
                              fullWidth
                              required
                              inputProps={{
                                dir: isRTL(l.code) ? "rtl" : "ltr",
                              }}
                              InputLabelProps={{
                                style: {
                                  textAlign: isRTL(l.code) ? "right" : "left",
                                },
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4} lg={4}>
                            <TextField
                              label={t("body")}
                              placeholder={t("announcementPlaceholders.body")}
                              className="input-field"
                              name={`body_${l.code}`}
                              value={langData[l.code]?.body || ""}
                              onChange={(e) =>
                                handleLangFieldChange(
                                  l.code,
                                  "body",
                                  e.target.value
                                )
                              }
                              fullWidth
                              required
                              inputProps={{
                                dir: isRTL(l.code) ? "rtl" : "ltr",
                              }}
                              InputLabelProps={{
                                style: {
                                  textAlign: isRTL(l.code) ? "right" : "left",
                                },
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4} lg={4}>
                            <TextField
                              label={t("link")}
                              placeholder={t("announcementPlaceholders.link")}
                              className="input-field"
                              name={`link_${l.code}`}
                              value={langData[l.code]?.link || ""}
                              onChange={(e) =>
                                handleLangFieldChange(
                                  l.code,
                                  "link",
                                  e.target.value
                                )
                              }
                              fullWidth
                              error={
                                !!langData[l.code]?.link &&
                                !isValidUrl(langData[l.code].link)
                              }
                              helperText={
                                !!langData[l.code]?.link &&
                                !isValidUrl(langData[l.code].link)
                                  ? t("invalidUrl")
                                  : ""
                              }
                            />
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                ))}
              </React.Suspense>
            )}

            {form.type === AnnouncementType.DOWNTIME && (
              <>
                <Grid item xs={12} sm={8} md={6}>
                  <DateTimePicker
                    label={t("fromUTC")}
                    className="input-field"
                    format={dateTimeFormat}
                    value={form.fromUTCtime ? new Date(form.fromUTCtime) : null}
                    onChange={(newValue) => {
                      handleDateChange("fromUTCtime", newValue);
                      handleDateChange(
                        "popupEndTime",
                        newValue,
                        true,
                        newValue
                      );
                      if (form.toUTCtime)
                        validateDates("toUTCtime", form.toUTCtime as Date);
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!errors.fromUTCtime,
                        helperText: errors.fromUTCtime,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={8} md={6}>
                  <DateTimePicker
                    label={t("toUTC")}
                    className="input-field"
                    format={dateTimeFormat}
                    value={form.toUTCtime ? new Date(form.toUTCtime) : null}
                    onChange={(newValue) => {
                      handleDateChange("toUTCtime", newValue);
                      if (form.fromUTCtime)
                        validateDates("fromUTCtime", form.fromUTCtime as Date);
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!errors.toUTCtime,
                        helperText: errors.toUTCtime,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={8} md={6}>
                  <DateTimePicker
                    label={t("popupEndTime")}
                    className="input-field"
                    format={dateTimeFormat}
                    value={
                      form.popupEndTime ? new Date(form.popupEndTime) : null
                    }
                    onChange={(newValue) =>
                      handleDateChange("popupEndTime", newValue)
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!errors.popupEndTime,
                        helperText: errors.popupEndTime,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={8} md={6}>
                  <TextField
                    label={t("announcementTable.platform")}
                    name="platform"
                    select
                    fullWidth
                    sx={{ marginTop: "7px" }}
                    value={form.platform}
                    onChange={handleChange}
                    required
                  >
                    {platforms.map((platform) => (
                      <MenuItem key={platform} value={platform}>
                        {t(`platformValues.${platform}`)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            )}

            {form.type === AnnouncementType.UPDATE && (
              <Grid item xs={12} sm={6} md={4} lg={4}>
                <TextField
                  label={t("version")}
                  className="input-field"
                  placeholder={t("announcementPlaceholders.version")}
                  name="version"
                  value={form.version}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={
                    !!form.version && !isValidVersion(form.version as string)
                  }
                  helperText={
                    !!form.version && !isValidVersion(form.version as string)
                      ? t("invalidVersion")
                      : ""
                  }
                />
              </Grid>
            )}

            {form.type &&
              form.type !== AnnouncementType.DOWNTIME &&
              form.type !== AnnouncementType.OTHER && (
                <Grid
                  item
                  xs={12}
                  sm={form.type === AnnouncementType.FEATURE ? 8 : 6}
                  md={form.type === AnnouncementType.FEATURE ? 8 : 4}
                  lg={form.type === AnnouncementType.FEATURE ? 8 : 4}
                >
                  <TextField
                    label={t("link")}
                    className="input-field"
                    placeholder={t("announcementPlaceholders.link")}
                    name="link"
                    value={form.link}
                    onChange={handleChange}
                    fullWidth
                    error={!!form.link && !isValidUrl(form.link as string)}
                    helperText={
                      !!form.link && !isValidUrl(form.link as string)
                        ? t("invalidUrl")
                        : ""
                    }
                  />
                </Grid>
              )}
          </Grid>
        </Box>
      </Box>
    </DataDialog>
  );
};

export default CreateAnnouncementDialog;
